/**
 * MyNewsDeskItemPage
 * @module pages/MyNewsDeskItemPage
 */

import React, { useContext, useEffect, useState } from 'react';
import ContentArea from 'components/framework/ContentArea';
import Layout from 'components/ui/Layout';
import { renderElementBasedOnEditMode } from 'common/helpers';
import { LocalizationContext } from 'context/localization.context';
import Breadcrumbs from 'components/framework/Breadcrumbs';
import {
	PublishInfoWrapper,
	Wrapper,
	SocialLink,
	FacebookIcon,
	TwitterIcon,
	LinedInIcon,
	EmailIcon,
	Image,
	RichText,
} from './MyNewsDeskItemPage.styles';
import { useSiteType } from 'context/siteType.context';
import Icon from 'components/ui/Icon';
import Button from 'components/ui/Button';

const MyNewsDeskItemPage = ({
	breadcrumbs,
	heading,
	preamble,
	text,
	highlight,
	publishDate,
	image,
	returnButton,
	saveAsPdfButton,
	contentArea,
	isInEditMode,
}) => {
	const { t } = useContext(LocalizationContext);
	const [windowUrl, setWindowUrl] = useState('');
	const siteType = useSiteType();
	const isLindab = siteType === 'Lindab';
	useEffect(() => {
		setWindowUrl(window.location.href);
	}, []);

	const renderSocialLinks = () => {
		return (
			<>
				<SocialLink
					href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
						windowUrl
					)}`}
					title={t('shared/socialmedia/shareonfacebook')}
					target="_blank"
				>
					<FacebookIcon />
				</SocialLink>
				<SocialLink
					href={`https://twitter.com/home?status=${encodeURIComponent(
						document.title
					)}%20${encodeURIComponent(windowUrl)}`}
					title={t('shared/socialmedia/shareontwitter')}
					target="_blank"
				>
					<TwitterIcon />
				</SocialLink>
				<SocialLink
					href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
						windowUrl
					)}`}
					title={t('shared/socialmedia/shareonlinkedin')}
					target="_blank"
				>
					<LinedInIcon />
				</SocialLink>
				<SocialLink
					href={`mailto:?subject=[title]&body=${encodeURIComponent(windowUrl)}`}
					title={t('shared/socialmedia/sharewithemail')}
					target="_blank"
				>
					<EmailIcon />
				</SocialLink>
			</>
		);
	};

	return (
		<main id="main-content">
			<Layout>
				{breadcrumbs && <Breadcrumbs links={breadcrumbs} />}
				{heading &&
					renderElementBasedOnEditMode(
						isInEditMode,
						'heading',
						'h1',
						{
							className:
								'font-heading mb-6 mt-8 font-bold text-h2 md:text-h1 text-blue md:w-8/12',
						},
						heading
					)}
				<PublishInfoWrapper className="mb-4">
					<Wrapper>
						{highlight && (
							<span className="py-1.5 px-4 text-p font-bold text-black bg-lavender rounded-lg mr-4">
								{highlight.text}
							</span>
						)}
						{publishDate && (
							<span className="text-p text-black">{publishDate}</span>
						)}
					</Wrapper>

					<Wrapper>{renderSocialLinks()}</Wrapper>
				</PublishInfoWrapper>
				{image && image.src && image.sizes.length > 0 && (
					<picture>
						<Image
							src={image.src}
							srcSet={`${image.sizes[0]} 800w, ${image.sizes[1]} 400w`}
							sizes="(max-width: 767px) 70vw, 800px"
							alt={image.alt ? image.alt : ''}
							className="rounded-card"
						/>
					</picture>
				)}

				{preamble &&
					renderElementBasedOnEditMode(
						isInEditMode,
						'preamble',
						'p',
						{ className: 'md:w-8/12 font-bold my-4' },
						preamble
					)}
				{text && (
					<RichText name="text" text={text} isInEditMode={isInEditMode} />
				)}

				<div className="flex flex-col sm:flex-row justify-between md:w-8/12">
					<Button url={returnButton.link} type="link" buttonColor="grey">
						<Icon icon="arrow" direction="left" size={1.25} />
						{returnButton.text}
					</Button>
					<Button
						url={saveAsPdfButton.link}
						type="link"
						buttonColor="blue"
						target="_blank"
					>
						{saveAsPdfButton.text}
						<Icon icon="download" size={1.25} />
					</Button>
				</div>
			</Layout>

			<ContentArea contentArea={contentArea} />
		</main>
	);
};

export default MyNewsDeskItemPage;
